import React from 'react';
import { Link } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface EmptyStateProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  /** The action that fills it: a button (`onClick`) or, to go to another page, a link (`href`). */
  action?: { label: string; onClick: () => void } | { label: string; href: string };
  /**
   * `default` for a page or a section of its own (dashed box). `compact` inside something that
   * already has edges: a card, a dialog, a popover or a table body (no box, less padding).
   */
  size?: 'default' | 'compact';
  className?: string;
  /** Anything else under the text, such as links (NotFoundPage, RecordPageState). */
  children?: React.ReactNode;
}

/**
 * Placeholder when a list, table, or section has no data.
 */
export function EmptyState({
  title,
  description,
  icon,
  action,
  size = 'default',
  className,
  children,
}: EmptyStateProps) {
  const compact = size === 'compact';
  return (
    <div
      data-size={size}
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'gap-2 px-4 py-6' : 'gap-3 rounded-lg border border-dashed px-6 py-12',
        className
      )}
    >
      <div
        className={cn(
          'flex items-center justify-center rounded-full bg-muted text-muted-foreground',
          compact ? 'size-8' : 'size-10'
        )}
      >
        {icon ?? <Inbox className={compact ? 'size-4' : 'size-5'} />}
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {description ? (
          <p className={cn('max-w-sm text-muted-foreground', compact ? 'text-xs' : 'text-sm')}>
            {description}
          </p>
        ) : null}
      </div>
      {action && 'href' in action ? (
        <Button asChild size={compact ? 'sm' : 'default'}>
          <Link to={action.href}>{action.label}</Link>
        </Button>
      ) : action ? (
        <Button type="button" size={compact ? 'sm' : 'default'} onClick={action.onClick}>
          {action.label}
        </Button>
      ) : null}
      {children}
    </div>
  );
}
